import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMail, FiPhone, FiMapPin, FiFacebook, FiTwitter, FiLinkedin, FiInstagram, FiYoutube } from 'react-icons/fi';
import api from '../../utils/api';
import './Footer.css';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [siteSettings, setSiteSettings] = useState({
    company_email: 'info@couriex.com',
    company_phone: '+1 (555) 123-4567',
    company_address: '123 Business St, City, Country',
    facebook_url: '#',
    twitter_url: '#',
    linkedin_url: '#',
    instagram_url: '#',
    youtube_url: '',
  });

  useEffect(() => {
    fetchSiteSettings();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const response = await api.get('/site-settings');
      const settings = response.data?.data || response.data || {};
      setSiteSettings((prev) => ({ ...prev, ...settings }));
    } catch (error) {
      console.error('Error fetching site settings:', error);
    }
  };

  const footerLinks = {
    company: [
      { path: '/about', label: 'About Us' },
      { path: '/services', label: 'Services' },
      { path: '/contact', label: 'Contact' },
    ],
    support: [
      { path: '/faq', label: 'FAQ' },
      { path: '/privacy-policy', label: 'Privacy Policy' },
      { path: '/terms-of-service', label: 'Terms of Service' },
    ],
  };

  const socialLinks = [
    { icon: FiFacebook, url: siteSettings.facebook_url, label: 'Facebook' },
    { icon: FiTwitter, url: siteSettings.twitter_url, label: 'Twitter' },
    { icon: FiLinkedin, url: siteSettings.linkedin_url, label: 'LinkedIn' },
    { icon: FiInstagram, url: siteSettings.instagram_url, label: 'Instagram' },
  ];

  if (siteSettings.youtube_url) {
    socialLinks.push({
      icon: FiYoutube,
      url: siteSettings.youtube_url,
      label: 'YouTube',
    });
  }

  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-content">
          <motion.div
            className="footer-section"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="footer-logo">Couriex</h3>
            <p className="footer-description">
              Professional courier and logistics services. Delivering excellence
              with speed and reliability.
            </p>
            <div className="footer-social">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.url}
                  aria-label={social.label}
                  className="footer-social-link"
                  whileHover={{ scale: 1.2, y: -2 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <social.icon />
                </motion.a>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="footer-section"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h4 className="footer-heading">Company</h4>
            <ul className="footer-links">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  <Link to={link.path} className="footer-link">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            className="footer-section"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h4 className="footer-heading">Support</h4>
            <ul className="footer-links">
              {footerLinks.support.map((link, index) => (
                <li key={index}>
                  <Link to={link.path} className="footer-link">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            className="footer-section"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h4 className="footer-heading">Contact</h4>
            <div className="footer-contact">
              <div className="footer-contact-item">
                <FiMail className="footer-contact-icon" />
                <a href={`mailto:${siteSettings.company_email}`}>
                  {siteSettings.company_email}
                </a>
              </div>
              <div className="footer-contact-item">
                <FiPhone className="footer-contact-icon" />
                <a href={`tel:${siteSettings.company_phone?.replace(/[^0-9+]/g, '')}`}>
                  {siteSettings.company_phone}
                </a>
              </div>
              <div className="footer-contact-item">
                <FiMapPin className="footer-contact-icon" />
                <span>{siteSettings.company_address}</span>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="footer-bottom">
          <p>&copy; {currentYear} Couriex. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

